<template>
  <div
    v-if="$route.matched.length > 0"
    id="app"
    class="app v-app"
  >
    <public-layout v-if="$route.meta && $route.meta.public">
      <router-view />
    </public-layout>
    <secure-layout v-else>
      <router-view />
    </secure-layout>
    <div class="svg-ui-syntax-sprites">
      <sprite-svg />
    </div>
  </div>
  <div
    v-else
    class="app app--is-loading"
  >
    <el-loading
      :color="'brand-primary'"
      :size="'48'"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import { mapGetters } from 'vuex';
  import { findLast } from 'lodash-es';
  import appConfig from '@/common/appConfig';
  import createStore from '@/common/store/createStore';
  import leaveCounterMixin from '@/common/mixins/leaveCounter.mixin';
  import { UPDATES_CLUSTER_STATUSES } from '@/common/constants/updates.constants';

  import PublicLayout from '@/common/layouts/Public.vue';
  import SecureLayout from '@/hci/layouts/Secure.vue';
  import SpriteSvg from '@/common/components/SpriteSvg.vue';
  import useAuthStore from '@/common/store/pinia/auth';
  import { mapState, mapStores } from 'pinia';
  import useThemeStore from '@/common/store/pinia/theme';
  import takeFirst from '@/common/utils/takeFirst';
  import useTasksStore from '@/common/store/pinia/tasks';
  import useVhsTasksStore from '@/common/store/pinia/vhsTasks';
  import useVhsNodesStore from '@/common/store/pinia/vhsNodes';
  import useAbgwMigrationsStore from '@/common/store/pinia/abgwMigrations';
  import useTotpSettingsStore from '@/common/store/pinia/totpSettings';

  export default defineComponent({
    name: 'App',
    components: {
      SpriteSvg,
      SecureLayout,
      PublicLayout
    },
    mixins: appConfig.disable_idle_timeout ? [] : [leaveCounterMixin],
    computed: {
      ...mapState(useAuthStore, ['isLoggedIn']),
      ...mapState(useThemeStore, ['themeData']),
      ...mapGetters({
        updateStatus: 'updates/updateStatus',
      }),
      ...mapStores(useTasksStore),
      ...mapStores(useVhsTasksStore),
      ...mapStores(useVhsNodesStore)
    },
    metaInfo() {
      const translate = this.$t.bind(this);
      const route = this.$route.matched.find(item => item.name === this.$route.name) || this.$route;

      const routeWithTitle = findLast(this.$route.matched, item => item.meta?.title);

      // @ts-ignore
      const title = typeof routeWithTitle?.meta.title === 'function' && routeWithTitle.meta.title(translate);

      return {
        title: title || route.meta?.title || route.name || '',
        titleTemplate: (titleChunk) => {
          // @ts-ignore
          const { productName: appTitle } = this.themeData;

          return titleChunk ? `${titleChunk} - ${appTitle}` : appTitle;
        }
      };
    },
    watch: {
      isLoggedIn: {
        immediate: true,
        handler(val) {
          if (val) {
            createStore(['authorized']);
            Promise.all([
              this.$store.$takeFirst('locations/FETCH_LOCATIONS'),
              this.$store.$takeFirst('locationNames/FETCH'),
              takeFirst(this.tasksStore.fetchItems),
              takeFirst(this.vhsTasksStore.fetchItems),
              takeFirst(this.vhsNodesStore.fetchItems)
            ]);
            useAbgwMigrationsStore().initNotifications();
            useTotpSettingsStore().fetchTotpSettings();
          } else {
            useTotpSettingsStore().resetTotpSettings();
          }
        }
      },
      updateStatus(currentStatus, prevStatus) {
        if (currentStatus === UPDATES_CLUSTER_STATUSES.UPTODATE
          && prevStatus === UPDATES_CLUSTER_STATUSES.UPDATING) {
          this.reloadPage();
        }
      }
    },
    methods: {
      reloadPage() {
        window.location.reload();
      }
    }
  });
</script>

<style lang="scss" scoped>
  .app {
    height: 100%;

    &--is-loading {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
